import { ObjectId } from "@novorender/api";
import { HierarcicalObjectReference } from "@novorender/data-js-api";
import pMap from "p-map";
import { Dispatch, MutableRefObject, SetStateAction, useEffect, useRef, useState } from "react";

import { Tree } from "components/objectTree/types";
import { getParentPaths } from "components/objectTree/utils";
import { byProperty, type ObjectDataAccess } from "dataAccess";
import { ModelTreeFilterObjects, ModelTreeNodeData } from "features/modelTree/types";
import { getDescendantsFilteredByParserTypeInfo, makeNode } from "features/modelTree/utils";
import { getFilePathFromObjectPath, getParentPath, isLeafNode, isPdfOrDrawingObject } from "utils/objectData";
import { iterateAsync } from "utils/search";

const MAX_SAMPLE_IDS = 200;
const MAX_CHILDREN_PER_NODE = 500;
const MAX_DEPTH_ITERATIONS = 20;

/**
 * Hard cap on how many children may be accumulated per frontier node.
 * Mirrors `maxScan` in features/modelTree/utils.ts.
 */
const MAX_TOTAL_CHILDREN_PER_NODE = 5000;

/** Max concurrent child listings per depth iteration on the object database. */
const MAX_CONCURRENT_LOADS = 5;

type LoadResult = {
    path: string;
    children: HierarcicalObjectReference[];
    childIterator?: AsyncIterableIterator<HierarcicalObjectReference>;
};

type BulkChildAccess = {
    findChildrenOf(parents: readonly string[], signal?: AbortSignal): AsyncIterableIterator<HierarcicalObjectReference>;
};

/**
 * Served and offline bim-index both answer a bulk children predicate. The object
 * database has no such query, so it keeps the level-by-level `getChildren` walk.
 */
function isBimIndexAccess(access: ObjectDataAccess): access is ObjectDataAccess & BulkChildAccess {
    return access.kind === "bim-index" || access.kind === "bim-index-offline";
}

/**
 * Bim-index reveal. Every unloaded ancestor is listed in one search. A folder that
 * fills the child cap is dropped and the folders still under the cap are requested
 * again, so a wide folder's remaining pages do not hold the reveal. `undefined`
 * means the run was aborted.
 */
async function loadFrontierInBulk(
    tree: Tree<ModelTreeNodeData>,
    dataAccess: ObjectDataAccess & BulkChildAccess,
    ancestorPaths: Set<string>,
    signal: AbortSignal,
    fileTypes: ModelTreeFilterObjects[] | undefined,
    skipPdfFiles: boolean | undefined,
    knownPdfObjectIdsRef: MutableRefObject<Pick<Set<ObjectId>, "has"> | undefined>,
): Promise<LoadResult[] | undefined> {
    const frontier: string[] = [];
    for (const path of ancestorPaths) {
        const realNode = tree[path];
        if (realNode?.loading || realNode?.children) {
            continue;
        }
        const isLeaf = realNode ? realNode.isLeaf : false;
        if (!isLeaf) {
            frontier.push(path);
        }
    }

    if (frontier.length === 0) {
        return [];
    }

    const childrenByParent = new Map<string, HierarcicalObjectReference[]>();
    const seenByParent = new Map<string, Set<string>>();
    for (const path of frontier) {
        seenByParent.set(path, new Set<string>(tree[path]?.children ?? []));
        childrenByParent.set(path, []);
    }

    let pendingParents = frontier.filter((path) => (seenByParent.get(path)?.size ?? 0) < MAX_TOTAL_CHILDREN_PER_NODE);

    while (pendingParents.length > 0) {
        if (signal.aborted) {
            return undefined;
        }

        const stillPending = new Set(pendingParents);
        let reissueWithoutCapped = false;

        for await (const child of dataAccess.findChildrenOf(pendingParents, signal)) {
            if (signal.aborted) {
                return undefined;
            }

            const parentPath = getParentPath(child.path);
            const childrenArr = childrenByParent.get(parentPath);
            const seenPaths = seenByParent.get(parentPath);
            if (!childrenArr || !seenPaths) {
                continue;
            }
            if (seenPaths.size >= MAX_TOTAL_CHILDREN_PER_NODE) {
                continue;
            }
            if (seenPaths.has(child.path)) {
                continue;
            }

            const isFileOrInsideFile = getFilePathFromObjectPath(parentPath);
            const pathFilter =
                fileTypes && !isFileOrInsideFile
                    ? new Set(getDescendantsFilteredByParserTypeInfo(parentPath, fileTypes))
                    : undefined;
            if (pathFilter?.size && !pathFilter.has(child.path)) {
                continue;
            }
            if (
                skipPdfFiles &&
                !isFileOrInsideFile &&
                (await isPdfOrDrawingObject(child, { knownPdfObjectIds: knownPdfObjectIdsRef.current }))
            ) {
                continue;
            }

            seenPaths.add(child.path);
            childrenArr.push(child);

            if (seenPaths.size < MAX_TOTAL_CHILDREN_PER_NODE) {
                continue;
            }

            stillPending.delete(parentPath);
            if (stillPending.size === 0) {
                break;
            }
            reissueWithoutCapped = true;
            break;
        }

        if (!reissueWithoutCapped) {
            break;
        }
        pendingParents = [...stillPending];
    }

    if (signal.aborted) {
        return undefined;
    }

    return frontier.map((path) => {
        const capped = (seenByParent.get(path)?.size ?? 0) >= MAX_TOTAL_CHILDREN_PER_NODE;
        const object = tree[path]?.data.object;
        const canResume = capped && (path === "" || object !== undefined);
        return {
            path,
            children: childrenByParent.get(path)!,
            childIterator: canResume
                ? childrenNotShown(dataAccess, path === "" ? undefined : object, seenByParent.get(path)!)
                : undefined,
        };
    });
}

/**
 * The folder's listing from the start, without the rows already shown. The bulk search
 * cannot be resumed for one folder, and a restarted listing that repeats those rows
 * would spend Load More's whole scan budget on them.
 *
 * Not given the reveal's signal: the iterator is kept on the node for Load More, and
 * the reveal's own tree update aborts that signal. Pages are fetched only as the
 * iterator is pulled, so an iterator nobody pulls makes no requests.
 */
async function* childrenNotShown(
    dataAccess: ObjectDataAccess,
    parent: HierarcicalObjectReference | undefined,
    shown: ReadonlySet<string>,
): AsyncIterableIterator<HierarcicalObjectReference> {
    for await (const child of dataAccess.getChildren(parent)) {
        if (!shown.has(child.path)) {
            yield child;
        }
    }
}

/**
 * When objects are selected in the 3D view (e.g. via lasso / area selection),
 * the object tree may not have loaded the subtree containing them.
 *
 * This hook resolves a sample of selected object IDs to their paths, computes
 * ancestor paths, and loads children for any "frontier" tree nodes (nodes
 * whose children haven't been loaded yet) that lie on those ancestor paths.
 * On the bim-index that is one bulk search. On the object database it is still
 * the level-by-level `getChildren` walk. Either way the chain is filled in so
 * that {@link getGroupStateForIdSet} can propagate selection state up to parent
 * folders (showing "some" instead of "none").
 */
export function useLoadAncestorsForSelection(
    tree: Tree<ModelTreeNodeData>,
    dataAccess: ObjectDataAccess | undefined,
    selectedIdArr: ObjectId[],
    setTree: Dispatch<SetStateAction<Tree<ModelTreeNodeData>>>,
    fileTypes?: ModelTreeFilterObjects[],
    skipPdfFiles?: boolean,
    knownPdfObjectIds?: Pick<Set<ObjectId>, "has">,
): { isLoading: boolean } {
    // Read through a ref, and absent from the dependencies below: a hit only spares a
    // metadata load, so a run that started before discovery finished stays correct.
    const knownPdfObjectIdsRef = useRef(knownPdfObjectIds);
    knownPdfObjectIdsRef.current = knownPdfObjectIds;
    const setTreeRef = useRef(setTree);
    setTreeRef.current = setTree;
    const selectedIdArrRef = useRef(selectedIdArr);
    selectedIdArrRef.current = selectedIdArr;
    const treeRef = useRef(tree);
    treeRef.current = tree;

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!dataAccess || selectedIdArr.length === 0) {
            setIsLoading(false);
            return;
        }

        // Quick check: any non-leaf nodes without children loaded and not
        // already being loaded by another source (e.g. selectNode)?
        // Include the root node so the hook can bootstrap from parentPath: "".
        const hasFrontierNodes = Object.values(treeRef.current).some((n) => !n.isLeaf && !n.children && !n.loading);

        // If not, the tree is fully expanded (or already loading) and there's nothing to do.
        if (!hasFrontierNodes) {
            setIsLoading(false);
            return;
        }

        const abortController = new AbortController();
        const signal = abortController.signal;

        setIsLoading(true);
        (async () => {
            try {
                // 1. Sample selected IDs and resolve to objects to get paths.
                //    First, resolve as many as possible from the existing tree to
                //    avoid redundant network requests (selectNode / expandOrSelectNode
                //    already loads the clicked object into the tree).
                const sampleIds = selectedIdArrRef.current.slice(0, MAX_SAMPLE_IDS);

                const resolvedObjects: { path: string }[] = [];
                const unresolvedIds: string[] = [];

                const treeNodesByIdSnapshot = new Map<number, string>();
                for (const node of Object.values(treeRef.current)) {
                    treeNodesByIdSnapshot.set(node.data.object.id, node.path);
                }

                for (const id of sampleIds) {
                    const path = treeNodesByIdSnapshot.get(id);
                    if (path !== undefined) {
                        resolvedObjects.push({ path });
                    } else {
                        unresolvedIds.push(String(id));
                    }
                }

                // Only search for IDs not already in the tree.
                if (unresolvedIds.length > 0) {
                    for await (const obj of dataAccess.findByProperties(byProperty("id", unresolvedIds), { signal })) {
                        resolvedObjects.push({ path: obj.path });
                    }

                    if (signal.aborted) {
                        return;
                    }
                }

                if (signal.aborted || resolvedObjects.length === 0) {
                    return;
                }

                // 2. Compute all unique ancestor paths from selected objects.
                const ancestorPaths = new Set<string>();
                for (const obj of resolvedObjects) {
                    for (const p of getParentPaths(obj.path)) {
                        ancestorPaths.add(p);
                    }
                }

                if (isBimIndexAccess(dataAccess)) {
                    const loaded = await loadFrontierInBulk(
                        treeRef.current,
                        dataAccess,
                        ancestorPaths,
                        signal,
                        fileTypes,
                        skipPdfFiles,
                        knownPdfObjectIdsRef,
                    );

                    if (signal.aborted || !loaded || loaded.length === 0) {
                        return;
                    }

                    setTreeRef.current((tree) => {
                        const newTree = { ...tree };
                        let changed = false;

                        for (const result of loaded) {
                            const node = newTree[result.path];
                            if (!node || node.children) {
                                continue;
                            }

                            const childPaths: string[] = [];
                            const seenChildPaths = new Set<string>();
                            const expectedParent = result.path ? `${result.path}/` : "";
                            for (const child of result.children) {
                                if (seenChildPaths.has(child.path)) {
                                    continue;
                                }
                                if (!child.path.startsWith(expectedParent) || child.path === result.path) {
                                    continue;
                                }
                                if (child.path.includes("/", expectedParent.length)) {
                                    continue;
                                }
                                seenChildPaths.add(child.path);
                                if (!newTree[child.path]) {
                                    newTree[child.path] = makeNode(child);
                                }
                                childPaths.push(child.path);
                            }

                            const childIterator = result.childIterator ?? node.data.childIterator;
                            newTree[result.path] = {
                                ...node,
                                expanded: true,
                                data: { ...node.data, childIterator },
                                children: childPaths,
                                hasMoreChildren: Boolean(childIterator),
                            };
                            changed = true;
                        }

                        return changed ? newTree : tree;
                    });
                    return;
                }

                // 3. Iteratively load children for frontier nodes on ancestor
                //    paths. Each iteration goes one level deeper. We maintain a
                //    lightweight working copy to track which nodes have been
                //    loaded without mutating the real tree yet.
                const allResults: LoadResult[] = [];

                // `object` is carried alongside the structure because `getChildren` lists by
                // object, not by path — a frontier node discovered during an earlier depth
                // iteration is not in `tree` yet, so its record is the only place its object is.
                const workingTree: Record<
                    string,
                    { children?: string[]; isLeaf: boolean; object: HierarcicalObjectReference }
                > = {};
                for (const [path, node] of Object.entries(treeRef.current)) {
                    workingTree[path] = { children: node.children, isLeaf: node.isLeaf, object: node.data.object };
                }

                for (let depth = 0; depth < MAX_DEPTH_ITERATIONS; depth++) {
                    if (signal.aborted) {
                        return;
                    }

                    // Find frontier nodes that are ancestors of selected objects.
                    // Skip nodes already being loaded by another source (e.g. selectNode)
                    // to avoid issuing duplicate network requests.
                    const frontier: string[] = [];
                    for (const path of ancestorPaths) {
                        const entry = workingTree[path];
                        if (entry && !entry.isLeaf && !entry.children) {
                            // Check the real tree for loading or children state set by selectNode/expandNode.
                            // Both checks are needed: `loading` is set at the start of an async load,
                            // `children` is set when the load completes. Either means the node is being
                            // handled elsewhere, so skip it to prevent concurrent duplicate loads that
                            // double memory consumption.
                            const realNode = treeRef.current[path];
                            if (realNode?.loading || realNode?.children) {
                                continue;
                            }
                            frontier.push(path);
                        }
                    }

                    if (frontier.length === 0) {
                        break;
                    }

                    // Load children for frontier nodes with a concurrency cap
                    // to avoid overwhelming the backend / browser connection limits.
                    const results = await pMap(
                        frontier,
                        async (path): Promise<LoadResult | null> => {
                            try {
                                // Reuse an existing iterator stored on the node (from a
                                // previous partial load) so we continue where we left off,
                                // mirroring the pattern used by loadChildren in modelTree/utils.
                                const existingNode = treeRef.current[path];
                                // Apply the same file-type filtering as loadChildren:
                                // only filter when fileTypes are active and the path is
                                // above file level. Matched locally for loadChildren's reason.
                                const isFileOrInsideFile = getFilePathFromObjectPath(path);
                                const pathFilter =
                                    fileTypes && !isFileOrInsideFile
                                        ? new Set(getDescendantsFilteredByParserTypeInfo(path, fileTypes))
                                        : undefined;
                                const iterator =
                                    existingNode?.data.childIterator ??
                                    dataAccess.getChildren(path === "" ? undefined : workingTree[path].object, signal);

                                const children: HierarcicalObjectReference[] = [];
                                let done = false;
                                // Budget by distinct paths, not raw records. Models with duplicate-path
                                // records (IFC/RVT exports) can return the same path many times; counting
                                // raw records would accumulate duplicates that get deduped anyway. Seeding
                                // from already-shown children (if any) ensures we don't re-add them.
                                const seenPaths = new Set<string>(existingNode?.children ?? []);

                                while (!done && !signal.aborted && seenPaths.size < MAX_TOTAL_CHILDREN_PER_NODE) {
                                    const [batch, batchDone] = await iterateAsync({
                                        iterator,
                                        count: MAX_CHILDREN_PER_NODE,
                                        abortSignal: signal,
                                    });
                                    for (const child of batch) {
                                        // Empty set means "nothing to narrow by" — see loadChildren.
                                        if (pathFilter?.size && !pathFilter.has(child.path)) {
                                            continue;
                                        }
                                        if (
                                            skipPdfFiles &&
                                            !isFileOrInsideFile &&
                                            (await isPdfOrDrawingObject(child, {
                                                knownPdfObjectIds: knownPdfObjectIdsRef.current,
                                            }))
                                        ) {
                                            continue;
                                        }
                                        // Skip paths we've already seen (duplicates or already-shown children).
                                        if (seenPaths.has(child.path)) {
                                            continue;
                                        }
                                        seenPaths.add(child.path);
                                        children.push(child);
                                    }
                                    done = batchDone;
                                }

                                return {
                                    path,
                                    children,
                                    childIterator: undefined,
                                };
                            } catch {
                                return null;
                            }
                        },
                        { concurrency: MAX_CONCURRENT_LOADS, signal },
                    );

                    if (signal.aborted) {
                        return;
                    }

                    // Apply to working tree for next iteration.
                    let anyNew = false;
                    for (const result of results) {
                        if (!result) {
                            continue;
                        }
                        const entry = workingTree[result.path];
                        if (!entry || entry.children) {
                            continue;
                        }

                        // Dedupe by path: the tree is keyed by path, so several
                        // sibling records sharing one path (common in IFC/RVT
                        // exports) must map to a single child entry. Letting a path
                        // in more than once renders the same node repeatedly and, if
                        // duplicates nest, makes tree traversals blow up. Mirrors the
                        // dedupe in appendDescendants (features/modelTree/utils).
                        // Also validate that each child is actually a direct child of
                        // this parent (not a sibling or unrelated node from search).
                        const childPaths: string[] = [];
                        const seenChildPaths = new Set<string>();
                        const expectedParent = result.path ? `${result.path}/` : "";
                        for (const child of result.children) {
                            if (seenChildPaths.has(child.path)) {
                                continue;
                            }
                            // Validate parent-child relationship
                            if (!child.path.startsWith(expectedParent) || child.path === result.path) {
                                continue;
                            }
                            // Ensure direct child (no additional "/" after parent prefix)
                            if (child.path.includes("/", expectedParent.length)) {
                                continue;
                            }
                            seenChildPaths.add(child.path);
                            if (!workingTree[child.path]) {
                                workingTree[child.path] = {
                                    object: child,
                                    children: undefined,
                                    isLeaf: isLeafNode(child),
                                };
                            }
                            childPaths.push(child.path);
                        }
                        workingTree[result.path] = { ...entry, children: childPaths };
                        allResults.push(result);
                        anyNew = true;
                    }

                    if (!anyNew) {
                        break;
                    }
                }

                if (signal.aborted || allResults.length === 0) {
                    return;
                }

                // 4. Apply all results to the real tree in one update.
                setTreeRef.current((tree) => {
                    const newTree = { ...tree };
                    let changed = false;

                    for (const result of allResults) {
                        const node = newTree[result.path];
                        if (!node || node.children) {
                            continue; // Already loaded by another source.
                        }

                        // Create child nodes, de-duplicating by path (see the
                        // working-tree loop above) so identical-path siblings
                        // collapse to a single child entry. Also validate that
                        // each child is actually a direct child of this parent.
                        const childPaths: string[] = [];
                        const seenChildPaths = new Set<string>();
                        const expectedParent = result.path ? `${result.path}/` : "";
                        for (const child of result.children) {
                            if (seenChildPaths.has(child.path)) {
                                continue;
                            }
                            // Validate parent-child relationship
                            if (!child.path.startsWith(expectedParent) || child.path === result.path) {
                                continue;
                            }
                            // Ensure direct child (no additional "/" after parent prefix)
                            if (child.path.includes("/", expectedParent.length)) {
                                continue;
                            }
                            seenChildPaths.add(child.path);
                            if (!newTree[child.path]) {
                                newTree[child.path] = makeNode(child);
                            }
                            childPaths.push(child.path);
                        }

                        // Update parent with children array and persist the
                        // pagination state needed for any follow-up loads.
                        // Preserve any existing iterator when this load does
                        // not provide a new one, so truncated/aborted loads
                        // do not accidentally mark the node as fully loaded.
                        // Derive hasMoreChildren from the effective iterator
                        // (same as appendDescendants in modelTree/utils) so
                        // the two fields can never get out of sync.
                        const childIterator = result.childIterator ?? node.data.childIterator;
                        newTree[result.path] = {
                            ...node,
                            expanded: true,
                            data: {
                                ...node.data,
                                childIterator,
                            },
                            children: childPaths,
                            hasMoreChildren: Boolean(childIterator),
                        };
                        changed = true;
                    }

                    return changed ? newTree : tree;
                });
            } catch {
                // Silently handle errors – the UI falls back to default
                // behavior (e.g. usePreloadDescendants / descendants arrays).
            } finally {
                if (!signal.aborted) {
                    setIsLoading(false);
                }
            }
        })();

        return () => {
            abortController.abort();
            setIsLoading(false);
        };
        // Re-run when the selection or data access changes. Tree is read through
        // treeRef so sibling hooks writing to tree state (usePreloadDescendants,
        // usePreloadOnExpand) don't cancel an in-flight OR-predicate search.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataAccess, selectedIdArr, fileTypes, skipPdfFiles]);

    return { isLoading };
}
